import type React from 'react';

import {getConfig} from 'coreUtils/utils';

/** Router location (React Router 7 or v5): the fields the slots compare. */
export type SlotLocation = {pathname: string; search?: string; key?: string};

export type NavigationAction = 'POP' | 'PUSH' | 'REPLACE';

/** A mounted page of `StrictLocaleLoaderRouter`. */
export type PageSlot = {
	/** React key: the page is the same instance as long as the id does not change. */
	id: number;
	Component: React.ComponentType | undefined;
	/**
	 * Location of the page, updated when the router swaps or updates it. The page renders with it while it is
	 * being left (the next page loads), parked, or shown again by the keep-alive; a parked page keeps the one it
	 * was left with.
	 */
	location: SlotLocation;
	locale?: string;
	/** Parked page shown again (back/forward), until the next navigation: its scroll is restored by `KeepAlive`. */
	isKeptAlive?: boolean;
};

/**
 * The page on screen and at most one parked page: hidden but still mounted (`<KeepAlive>`), shown again as it
 * was left when the user goes back to its history entry.
 */
export type PageSlots = {current: PageSlot; parked?: PageSlot};

export type SlotNavigation = {
	/** Page component of the new location. */
	Component: React.ComponentType | undefined;
	location: SlotLocation;
	action: NavigationAction;
	locale?: string;
	/** The current page is one to keep alive (`isKeepAliveRoute`). */
	keepAlive: boolean;
};

/**
 * Store routes (`receiveRoute`, e.g. `Search-Show?cgid=…`) of the pages kept alive when the user leaves them,
 * from `app.keepAlive.routes` of the brand config (regular expressions).
 *
 * @returns {RegExp[]} - Patterns, empty when the brand keeps no page alive
 */
export const getKeepAliveRoutes = (): RegExp[] =>
	((getConfig()?.app?.keepAlive?.routes as string[] | undefined) || []).map((pattern) => new RegExp(pattern));

/**
 * @param {string | null | undefined} route - Store route of the page on screen
 * @param {RegExp[]} patterns - `getKeepAliveRoutes()`
 * @returns {boolean} - The page is kept alive when the user leaves it
 */
export const isKeepAliveRoute = (route: string | null | undefined, patterns: RegExp[]): boolean =>
	!!route && patterns.some((pattern) => pattern.test(route));

/**
 * Slots after a navigation:
 * - back/forward (`POP`) to the history entry of the parked page: it becomes the current page again, on the
 *   location it was left with (until the next navigation), and the page on screen is unmounted;
 * - leaving a page to keep alive for another page component: it is parked, the new page gets a new slot;
 * - otherwise the current slot renders the new page (same instance when the component does not change, as
 *   without keep-alive). The parked page is discarded when the new page has its component (another PLP, the
 *   same URL again: one instance at a time, they share the search state) or another locale.
 *
 * The history entry is the location key plus the pathname: the PLP rewrites its query string (load more,
 * `replaceState` keeping the history state) without the router knowing.
 *
 * @param {PageSlots} slots - Current slots
 * @param {SlotNavigation} navigation - The navigation
 * @returns {PageSlots} - Next slots
 */
export const navigateSlots = ({current, parked}: PageSlots, navigation: SlotNavigation): PageSlots => {
	const {Component, location, action, locale, keepAlive} = navigation;

	if (parked && action === 'POP' && location.key === parked.location.key && location.pathname === parked.location.pathname) {
		return {current: {...parked, isKeptAlive: true}};
	}

	if (keepAlive && current.Component && Component !== current.Component) {
		return {
			current: {id: Math.max(current.id, parked?.id ?? 0) + 1, Component, location, locale},
			parked: {id: current.id, Component: current.Component, location: current.location, locale: current.locale},
		};
	}

	const next = {current: {id: current.id, Component, location, locale}};

	return parked && parked.Component !== Component && parked.locale === locale ? {...next, parked} : next;
};
